import React, { useContext, useEffect, useRef, useState } from 'react';
import ItemNew from './ItemNew';
import LoaderContext from './LoaderContext';
import LoginPage from '../pages/LoginPage';
import { buildEntreeTagColumns, tagArray } from '../utils/helpers';
import { useCravings } from '../hooks/useCravings';

type Props = {
  categoryId: number;
  categoryName: string;
  user: any;
  location: { lat: number; lng: number };
  handleLogin(user): void;

};

const CategoryFeed: React.FC<Props> = ({
                                         categoryId,
                                         categoryName,
                                         user,
                                         location,
                                         handleLogin
                                       }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const { showLoader, hideLoader } = useContext(LoaderContext);
  const { cravedIds, handleToggleCraving } = useCravings(user);
  const [error, setError] = useState('');
  const [nearByItems, setNearByItems] = useState<any[]>([]);
  const [topItems, setTopItems] = useState<any[]>([]);
  const [tags, setTags] = useState<number[]>([]);
  const [showFilter, setShowFilter] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  // Load when category changes or login state changes
  useEffect(() => {
    // reset selected tags and close the filter drawer on category switch
    setTags([]);
    setShowFilter(false);
    if (categoryId) loadItems([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, user.isLogged]);



  const onTagToggle = (id: number) => {
    const newTags = tags.includes(id) ? tags.filter(t => t !== id) : [...tags, id];
    setTags(newTags);
    // smooth scroll to the list after picking first tag
    if (!tags.includes(id) && ref.current) {
      window.scrollTo({ top: ref.current.offsetTop, behavior: 'smooth' });
    }
    loadItems(newTags);
  };

  const loadItems = (selectedTags: number[]) => {
    showLoader();

    const headers = new Headers({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    });
    if (user.isLogged) headers.append('Authorization', 'Bearer ' + user.token);

    const miles = Number(localStorage.getItem('selectedDistance') || 0);

    fetch(
      `${process.env.REACT_APP_SERVER_URL}/yolos?category=${categoryId}` +
      `&latitude=${location.lat}&longitude=${location.lng}` +
      `&tags=${selectedTags.join()}` +
      `&distanceMiles=${miles}` +
      `&v=${Date.now()}`,
      { method: 'GET', headers }
    )
      .then(res => res.json())
      .then(
        result => {
          setNearByItems(result.nearBy || []);
          setTopItems(result.top || []);
          setError('');
          hideLoader();
          setShowLogin(false);
        },
        err => {
          setError(err?.message || 'Failed to load');
          hideLoader();
        }
      );
  };

  const syncUpvoteCount = (id: number, upvoteCount: number) => {
    setNearByItems(prev => prev.map(it => it.id === id ? { ...it, upvoteCount } : it));
    setTopItems(prev => prev.map(it => it.id === id ? { ...it, upvoteCount } : it));
  };

  const handleUpvote = (id: number) => {
    if (!user.isLogged) return setShowLogin(true);
    showLoader();
    fetch(`${process.env.REACT_APP_SERVER_URL}/items/${id}/upvote`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: 'Bearer ' + user.token,
      }),
      body: JSON.stringify({ userId: user.id }),
    })
      .then(r => r.json())
      .then(
        () => {
          setNearByItems(prev => prev.map(it => it.id === id ? { ...it, upvoted: true, upvoteCount: it.upvoteCount + 1 } : it));
          setTopItems(prev => prev.map(it => it.id === id ? { ...it, upvoted: true, upvoteCount: it.upvoteCount + 1 } : it));
          hideLoader();
        },
        () => hideLoader()
      );
  };

  const handleRemoveUpvote = (id: number) => {
    if (!user.isLogged) return setShowLogin(true);
    showLoader();
    fetch(`${process.env.REACT_APP_SERVER_URL}/items/${id}/removeUpvote`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: 'Bearer ' + user.token,
      }),
      body: JSON.stringify({ userId: user.id }),
    })
      .then(r => r.json())
      .then(
        () => {
          setNearByItems(prev => prev.map(it => it.id === id ? { ...it, upvoted: false, upvoteCount: it.upvoteCount - 1 } : it));
          setTopItems(prev => prev.map(it => it.id === id ? { ...it, upvoted: false, upvoteCount: it.upvoteCount - 1 } : it));
          hideLoader();
        },
        () => hideLoader()
      );
  };


  // ---------- TAGS: build 2 columns, with an ENTREES-only override ----------
  const allTags = tagArray.filter(t => t.category === categoryId);
  const totalTagsCount = allTags.length;

  let leftTags = allTags;
  let rightTags: typeof allTags = [];
  if (categoryId === 1) {
    const entreeColumns = buildEntreeTagColumns(allTags);
    leftTags = entreeColumns.types;
    rightTags = entreeColumns.nationalities;
  } else if (categoryId === 14) {
    // Fast Food: left 92–122, right 123–150
    leftTags = allTags.filter(t => t.id >= 92 && t.id <= 122);
    rightTags = allTags.filter(t => t.id >= 123 && t.id <= 150);
  } else {
    // Generic 2-column split for other categories
    const mid = Math.ceil(allTags.length / 2);
    leftTags = allTags.slice(0, mid);
    rightTags = allTags.slice(mid);
  }

  const renderTag = (tag: { id: number; name: string }) => {
    const checked = tags.includes(tag.id);
    return (
      <li className="tag-filter" key={tag.id}>
        <input
          type="checkbox"
          id={`tags${tag.id}`}
          onChange={() => onTagToggle(tag.id)}
          checked={checked}
        />
        <label htmlFor={`tags${tag.id}`}>{tag.name}</label>
      </li>
    );
  };

  const leftList = leftTags.map(renderTag);
  const rightList = rightTags.map(renderTag);

  const handleNotificationClick = async (id: number) => {
    sendNotification(id);
  };

  const sendNotification = (id: number) => {
    try {
      const stored = localStorage.getItem('YoloMeal');
      console.log(stored);
      if (stored) {
        showLoader();
        const userData = JSON.parse(stored);
        fetch(
          process.env.REACT_APP_SERVER_URL +
          `/itemExpired?itemId=${id}&userId=${userData.id}`,
          {
            method: 'GET',
            headers: new Headers({
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: 'Bearer ' + userData.token,
            })
          }
        )
          .then(
            (result) => {

              if (nearByItems != null) {
                setNearByItems(
                  nearByItems.map((item) => (item.id === id ? { ...item, expired: true } : item))
                );
                console.log(nearByItems);
              } else {
                setTopItems(
                  topItems.map((item) => (item.id === id ? { ...item, expired: true } : item))
                );
                console.log(topItems);
              }

              hideLoader();
            },
            (error) => {
              hideLoader();
              console.log(error);
            }
          );
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  if (showLogin && !user.isLogged) {
    return (
      <LoginPage
        user={user}
        handleLogin={handleLogin}
        showBack={true}
        showHome={false}
        backgroundImage={true}
        handleBack={() => setShowLogin(false)}
      />
    );
  }

  return (
    <>
      {totalTagsCount > 0 && !showFilter && (
        <div
          className="my-pagebox tag-filter-panel pink-button text-center"
          style={{ marginTop: '30px' }}
          onClick={() => setShowFilter(true)}
        >
          Click here to filter your choices
        </div>
      )}

      {totalTagsCount > 0 && showFilter && (
        <div className="my-pagebox tag-filter-panel" style={{ marginTop: '30px' }}>
          <div className="checkbox-wrapper">
            <div className="checkbox-column">
              {categoryId === 1 && <h3 className="tag-column-heading">Types</h3>}
              <ul className="f-18 checkbox-list">{leftList}</ul>
            </div>
            <div className="checkbox-column">
              {categoryId === 1 && <h3 className="tag-column-heading">Nationalities</h3>}
              <ul className="f-18 checkbox-list">{rightList}</ul>
            </div>
          </div>
        </div>
      )}

      <div ref={ref} />

      <h1 className="text-center pt-3 pb-2 uppercase">{categoryName}</h1>

      {nearByItems.length === 0 && (
        <h3 className="f-38 light text-center font-poppins" style={{ marginTop: '20px' }}>
          Help everyone find the BEST by saving one of your TOP 3 here!
        </h3>
      )}

      <div className="categoryItem card-container">
        {nearByItems.map(
          (item) =>
            item.yolos?.length > 0 &&
            item.yolos[0].id !== null && (
              <div key={item.id} className="categoryItemWidth">
                <ItemNew
                  handleLogin={() => setShowLogin(true)}
                  item={item}
                  editable={user.id == 6 || user.id == 7 || user.id == 514}
                  isLogged={user.isLogged}
                  user={user}
                  page={"categoryFeed"}
                  showRestaurant={true}
                  handleUpvote={handleUpvote}
                  handleRemoveUpvote={handleRemoveUpvote}
                  handleNotificationClick={handleNotificationClick}
                  restaurentPage={false}
                  loadData={() => loadItems(tags)}
                  syncUpvoteCount={syncUpvoteCount}
                  handleMainFilter={setShowFilter}
                    craved={cravedIds.has(item.id)}
                  handleToggleCraving={handleToggleCraving}
                    handleLoginPrompt={() => setShowLogin(true)}

                />
              </div>
            )
        )}
      </div>

      {error && <p>{error}</p>}
    </>
  );
};

export default CategoryFeed;
